/* Zpráva k odkazu na doporučení — co poradce opravdu pošle na WhatsApp.
 *
 * PROČ TO EXISTUJE (UAT 8/2026, majitel):
 * „Když dám vygenerovat doporučení a kliknu na to, tak tam je jenom odkaz.
 *  Nevím, co mám poslat klientovi na WhatsApp. Doporučení proč, co?
 *  Vyplňte mi to, jak to vyplňte."
 * Dosud se do schránky kopírovala holá URL a v bublině svítilo
 * „Dobrý den X, posílám odkaz: …" — klient netušil, co s tím.
 *
 * ODMĚNA (15. 10. 2026, SPEC-STAVBA §5.5): plošná částka v šabloně NENÍ a nebude
 * — odměňovat systematické přivádění klientů se blíží zprostředkování. Částku si
 * zadá KAŽDÝ poradce sám v Nastavení → Doporučení (výchozí prázdná) a do zprávy
 * přijde jen ta jeho (`odmena`), jen „za tip“ (se schůzkou / každý předaný),
 * nikdy za smlouvu ani objem. Bez částky věta jen poděkuje. Věty skládá
 * lib/doporuceni/odmenaTipare.js — stejné jako na webu /d/ a v kině.
 *
 * Modul je čistý (bez Reactu a bez API) — testovatelný a snadno přepsatelný.
 */
import { cisloProVytaceni } from "@/lib/telefonCislo";
import { vetaOdmeny } from "@/lib/doporuceni/odmenaTipare";

/* PĚT JAZYKŮ ZPRÁVY (14. 10. 2026). Do té doby uměla šablona jen češtinu
 * a slovenštinu (`lang === "sk" ? … : …`) — vietnamský nebo ukrajinský klient
 * dostal českou zprávu. Jazyk zprávy = jazyk, ve kterém klient čte; volající
 * ho smí zvolit jinak než jazyk aplikace (český poradce, vietnamská klientka).
 *
 * VYKÁNÍ a ROD. Věta „Kdybyste … věděl o někom“ předpokládala klienta muže —
 * a majitel posílá odkaz „té klientce“. Text je teď bez rodu na obou stranách
 * (klient „znáte“, poradce „ozvu se osobně“, ne „sám“).
 *
 * Oslovení: české jméno se skloňuje do 5. pádu, ostatní jazyky ne — vokativ
 * řeší volající (osloveniProZpravu), sem přijde hotový tvar.
 *
 * KOLO 2 (15. 10. 2026, SPEC-STAVBA §5.1–5.4, cs a sk doslova): prosba o pomoc
 * („pomůžete mi?“), konkrétní životní situace místo „znáte někoho?“, co odkaz
 * udělá („otevře krátký formulář“), slib „zavolám jednou“ a věta odměny.
 * Pozdrav má VŽDY čárku za „Dobrý den“ („Dobrý den, Petře,“). Bez jména jen
 * „Dobrý den,“ a text pokračuje malým písmenem. Žádné „čeká miminko“
 * (zdravotní údaj) — „zakládá rodinu“. */
const SABLONY = {
  cs: {
    pozdrav: (j) => (j ? `Dobrý den, ${j},` : "Dobrý den,"),
    prosba: "děkuji za důvěru. Mám jednu prosbu: pomůžete mi?",
    situace: "Napadá Vás někdo, kdo teď zakládá rodinu, řeší bydlení nebo konec fixace, mění práci, chce mít jasno v pojistkách nebo myslí na důchod?",
    jak: "Stačí mi jméno a telefon, zabere to minutu. Odkaz otevře krátký formulář:",
    potom: "Nemusíte nic vysvětlovat ani nikoho přemlouvat. Zavolám jednou, osobně a nezávazně. Když nebude mít zájem, víc se neozvu.",
    dik: "Moc mi tím pomůžete.",
    sms: (o) => `${o ? `Dobrý den, ${o}, mám` : "Dobrý den, mám"} prosbu: napadá Vás někdo, kdo řeší bydlení, fixaci, rodinu, novou práci nebo pojistky? Pošlete mi jen jméno a telefon, zabere to minutu:`,
    smsPotom: "Zavolám jednou a nezávazně.",
    smsDik: "Děkuji!",
    predmet: "Malá prosba, zabere minutu",
  },
  sk: {
    pozdrav: (j) => (j ? `Dobrý deň, ${j},` : "Dobrý deň,"),
    prosba: "ďakujem za dôveru. Mám jednu prosbu: pomôžete mi?",
    situace: "Napadá Vás niekto, kto teraz zakladá rodinu, rieši bývanie alebo koniec fixácie, mení prácu, chce mať jasno v poistkách alebo myslí na dôchodok?",
    jak: "Stačí mi meno a telefón, zaberie to minútu. Odkaz otvorí krátky formulár:",
    potom: "Nemusíte nič vysvetľovať ani nikoho presviedčať. Zavolám raz, osobne a nezáväzne. Keď nebude mať záujem, viac sa neozvem.",
    dik: "Veľmi mi tým pomôžete.",
    sms: (o) => `${o ? `Dobrý deň, ${o}, mám` : "Dobrý deň, mám"} prosbu: napadá Vás niekto, kto rieši bývanie, fixáciu, rodinu, novú prácu alebo poistky? Pošlite mi len meno a telefón, zaberie to minútu:`,
    smsPotom: "Zavolám raz a nezáväzne.",
    smsDik: "Ďakujem!",
    predmet: "Malá prosba, zaberie minútu",
  },
  en: {
    pozdrav: (j) => (j ? `Hello ${j},` : "Hello,"),
    prosba: "Thank you for your trust. I have one favour to ask: would you help me?",
    situace: "Does anyone come to mind who is starting a family, dealing with housing or a fixed rate ending, changing jobs, wanting clarity on their insurance or thinking about retirement?",
    jak: "A name and a phone number are all I need, it takes a minute. The link opens a short form:",
    potom: "You do not need to explain anything or persuade anyone. I will call once, personally and with no obligation. If they are not interested, I will not get in touch again.",
    dik: "It would help me a lot.",
    sms: (o) => `${o ? `Hello ${o}, a` : "Hello, a"} small favour: does anyone come to mind who is dealing with housing, a fixed rate ending, family, a new job or insurance? Just send me a name and a phone number, it takes a minute:`,
    smsPotom: "I will call once, with no obligation.",
    smsDik: "Thank you!",
    predmet: "A small favour, it takes a minute",
  },
  uk: {
    pozdrav: (j) => (j ? `Добрий день, ${j},` : "Добрий день,"),
    prosba: "дякую за довіру. Маю до Вас одне прохання: допоможете мені?",
    situace: "Чи знаєте Ви когось, хто зараз створює сім'ю, вирішує питання житла чи кінця фіксації, змінює роботу, хоче розібратися у страховках або думає про пенсію?",
    jak: "Мені достатньо імені та номера телефону, це займе хвилину. Посилання відкриє коротку форму:",
    potom: "Нічого не потрібно пояснювати чи когось умовляти. Я зателефоную один раз, особисто і без жодних зобов'язань. Якщо людина не зацікавиться, більше не турбуватиму.",
    dik: "Ви мені дуже допоможете.",
    sms: (o) => `${o ? `Добрий день, ${o}, маю` : "Добрий день, маю"} прохання: чи знаєте Ви когось, хто вирішує питання житла, кінця фіксації, сім'ї, нової роботи чи страховок? Надішліть мені лише ім'я та телефон, це займе хвилину:`,
    smsPotom: "Я зателефоную один раз і без зобов'язань.",
    smsDik: "Дякую!",
    predmet: "Невелике прохання, це займе хвилину",
  },
  vi: {
    pozdrav: (j) => (j ? `Xin chào anh/chị ${j},` : "Xin chào anh/chị,"),
    prosba: "cảm ơn anh/chị đã tin tưởng. Tôi có một lời nhờ: anh/chị giúp tôi một việc nhé?",
    situace: "Anh/chị có biết ai đang lập gia đình, lo chuyện nhà ở hoặc sắp hết lãi suất cố định, đổi việc, muốn rõ về bảo hiểm hay nghĩ đến lương hưu không?",
    jak: "Tôi chỉ cần tên và số điện thoại, mất khoảng một phút. Đường dẫn sẽ mở một biểu mẫu ngắn:",
    potom: "Anh/chị không cần giải thích hay thuyết phục ai. Tôi sẽ gọi một lần, trực tiếp và không ràng buộc. Nếu họ không quan tâm, tôi sẽ không liên lạc nữa.",
    dik: "Như vậy là anh/chị đã giúp tôi rất nhiều.",
    sms: (o) => `${o ? `Xin chào anh/chị ${o}, tôi` : "Xin chào anh/chị, tôi"} có một lời nhờ: anh/chị có biết ai đang lo nhà ở, lãi suất cố định, gia đình, việc mới hay bảo hiểm không? Chỉ cần gửi tôi tên và số điện thoại, mất một phút:`,
    smsPotom: "Tôi sẽ gọi một lần, không ràng buộc.",
    smsDik: "Cảm ơn anh/chị!",
    predmet: "Một lời nhờ nhỏ, chỉ mất một phút",
  },
};

/** Jazyky, ve kterých zpráva existuje. Cokoli jiného → čeština. */
export const JAZYKY_ZPRAVY = Object.keys(SABLONY);

const sablona = (lang) => SABLONY[lang] || SABLONY.cs;

/**
 * Sestaví zprávu k odkazu na doporučení (WhatsApp / e-mail).
 *
 * @param {object} o
 * @param {string} o.osloveni  jméno ve tvaru, jakým se má klient oslovit
 * @param {string} o.odkaz     plná URL formuláře
 * @param {string} o.poradce   podpis (jméno, pod ním pozice — podpisDoporuceni); prázdné = bez podpisu
 * @param {string} o.lang      "cs" | "sk" | "en" | "uk" | "vi" (jazyk ZPRÁVY)
 * @param {object|null} o.odmena  odměna tipaři poradce (useOdmenaTipare); null = jen poděkování
 * @returns {string} text připravený k vložení do WhatsApp / SMS / e-mailu
 *
 * Bez odkazu vrací prázdný řetězec — poslat pozvánku bez odkazu nedává smysl
 * a tiché „napiš to bez URL" by poradce zjistil až od naštvaného klienta.
 */
export function zpravaDoporuceni({ osloveni = "", odkaz = "", poradce = "", lang = "cs", odmena = null } = {}) {
  const url = String(odkaz ?? "").trim();
  if (!url) return "";

  const jmeno = String(osloveni ?? "").trim();
  const s = sablona(lang);
  const podpis = String(poradce ?? "").trim();

  const radky = [
    s.pozdrav(jmeno), s.prosba, "",
    s.situace, "",
    s.jak, url, "",
    s.potom, `${vetaOdmeny(odmena, lang)} ${s.dik}`,
  ];
  if (podpis) {
    radky.push("", podpis);
  }
  return radky.join("\n");
}

/**
 * Krátká SMS s odkazem (SPEC §5.2) — jeden odstavec, podpis jen jménem
 * (první řádek podpisu). Bez odkazu "".
 */
export function smsDoporuceni({ osloveni = "", odkaz = "", poradce = "", lang = "cs", odmena = null } = {}) {
  const url = String(odkaz ?? "").trim();
  if (!url) return "";
  const s = sablona(lang);
  const jmenoPodpisu = String(poradce ?? "").trim().split("\n")[0].trim();
  return [s.sms(String(osloveni ?? "").trim()), url, s.smsPotom, vetaOdmeny(odmena, lang), s.smsDik, jmenoPodpisu]
    .filter(Boolean).join(" ");
}

/** Předmět e-mailu s odkazem (SPEC §5.4): „Malá prosba, zabere minutu“. */
export function predmetDoporuceni(lang = "cs") {
  return sablona(lang).predmet;
}

/* Jméno účtu, které NENÍ jméno člověka (seed „Admin“, testovací účty) —
 * stejné pravidlo jako podpis zprávy s podklady (lib/analyzaV2/podkladyKlienta.js). */
const RE_JMENO_UCTU = /^(admin|administr[aá]tor|spr[aá]vce|test|tester|user|u[zž]ivatel|demo|poradce)$/i;
const cisty = (v) => { const s = String(v ?? "").trim(); return /^(undefined|null|none|nan)$/i.test(s) ? "" : s; };

/**
 * Podpis zprávy z profilu poradce: jméno, pod ním pozice. Bez jména člověka
 * (prázdné, „Admin“, e-mail) "" — podpis se vynechá, nic se nedomýšlí.
 */
export function podpisDoporuceni(profil) {
  const p = profil && typeof profil === "object" ? profil : {};
  const jmeno = cisty(p.name);
  if (!jmeno || jmeno.includes("@") || RE_JMENO_UCTU.test(jmeno)) return "";
  const pozice = cisty(p.position);
  return pozice ? `${jmeno}\n${pozice}` : jmeno;
}

/**
 * Odkaz na WhatsApp s předvyplněnou zprávou.
 * Bez telefonu vrací wa.me/?text=… — otevře WhatsApp s textem a nechá poradce
 * vybrat příjemce ručně (lepší než mrtvé tlačítko).
 *
 * ČÍSLO ŘEŠÍ lib/telefonCislo.js. Dřív se tu jen odmazaly nečíslice, takže
 * uložené devítimístné číslo („777 123 456") šlo do odkazu bez předvolby a
 * wa.me nikoho nenašel — poradce klikl a nic. Předvolbu doplní ZEMĚ PORADCE,
 * nikdy jazyk aplikace.
 *
 * @param {string} telefon  co je uložené u klienta
 * @param {string} text     tělo zprávy (NEODESÍLÁ se, jen předvyplní)
 * @param {string} [zeme]   kód země poradce; vynechat = vzít ze store tenantu
 */
export function whatsappOdkaz(telefon, text, zeme) {
  const zprava = encodeURIComponent(String(text ?? ""));
  const cislo = cisloProVytaceni(telefon, zeme);
  return cislo ? `https://wa.me/${cislo}?text=${zprava}` : `https://wa.me/?text=${zprava}`;
}
